import React, { ReactElement, useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import useApi from '../../hooks/use-api';
import api from '../../api/api';
import SidebarItem, { SidebarItemProps } from './SidebarItem'
import { buildAuthenticatedFileUrl, isAdmin, isUserPage } from '../../utils/utils';
import { AppRoutes, UserType } from '../../utils/props';

interface SidebarProps {
  userName: string
  items: SidebarItemProps[]
  backgroundColor?: string
  tagId: number
}

const Sidebar = ({ userName, items, backgroundColor = 'bg-white' }: SidebarProps): ReactElement => {
  const navigate = useNavigate();
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [photoUploadError, setPhotoUploadError] = useState<string>('');
  const [activeHref, setActiveHref] = useState<string | undefined>(undefined);
  const imagesBasePath = `${process.env.PUBLIC_URL || ''}/images`;

  const hashHrefs = items.filter((item) => item.href.startsWith('#')).map((item) => item.href);
  const hashHrefsKey = hashHrefs.join(',');

  useEffect(() => {
    if (hashHrefs.length === 0) {
      setActiveHref(undefined);
      return;
    }

    const threshold = 80;

    const handleScroll = () => {
      // Scrollspy: the active item is the last section whose top has
      // reached the threshold, so something is always active, even once
      // its heading has scrolled past out of view.
      let currentActive = hashHrefs[0];
      for (const href of hashHrefs) {
        const section = document.getElementById(href.slice(1));
        if (!section) continue;
        if (section.getBoundingClientRect().top <= threshold) {
          currentActive = href;
        }
      }
      setActiveHref(currentActive);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hashHrefsKey]);

  function logout() {
    localStorage.clear();
    navigate(AppRoutes.Login);
  }

  const getUser = useApi({
    path: '/user',
    callback: (data) => {
      const nextPhotoUrl = data.photo || data.photo_url || '';
      if (nextPhotoUrl) {
        setAvatarUrl(nextPhotoUrl);
        localStorage.setItem('photo', JSON.stringify(nextPhotoUrl));
      }
    }
  });

  useEffect(() => {
    const storedPhoto = (localStorage.getItem('photo') ?? '').replace(/['"]+/g, '');
    if (storedPhoto) {
      setAvatarUrl(storedPhoto);
    }
    getUser.get();
  }, [])

  async function updateProfilePicture(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoUploadError('');

    const formData = new FormData()
    formData.append('photo', file)

    const storedToken: string | null = localStorage.getItem('token');
    const headers = storedToken ? { Authorization: `Bearer ${JSON.parse(storedToken)}` } : {};
    const currentUserType = (localStorage.getItem('type') ?? '').replace(/['"]+/g, '')
    const photoUploadPath = currentUserType === UserType.Admin.toString() ? '/admin/photo' : '/company/photo'

    try {
      const response = await api.post(photoUploadPath, formData, { headers });
      const nextPhotoUrl =
        response?.data?.photo ||
        response?.data?.photo_url ||
        response?.data?.url ||
        response?.data?.data?.photo ||
        response?.data?.data?.photo_url ||
        '';

      if (nextPhotoUrl) {
        setAvatarUrl(nextPhotoUrl)
        localStorage.setItem('photo', JSON.stringify(nextPhotoUrl));
      } else {
        getUser.get();
      }
    } catch (error) {
      const errorMessage =
        (error as any)?.response?.data?.error ||
        (error as any)?.response?.data?.message ||
        'Failed to upload profile picture. Please try again.';
      setPhotoUploadError(errorMessage);
    }

    e.target.value = '';
  }

  return (
    <div className="mt-5 h-full">
      <div className="md:hidden">
        <div className="mb-2 flex items-center gap-3 md:hidden">
          <div className="flex h-[60px] w-[60px] items-center justify-center rounded-full bg-blue-500">
            <img src={buildAuthenticatedFileUrl(avatarUrl) || `${imagesBasePath}/default-avatar.svg`} alt="avatar" className="h-full w-full rounded-full object-cover" />
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-semibold leading-tight text-black">{userName}</span>
            <label
              htmlFor="profile-upload"
              className="flex cursor-pointer items-center gap-1 text-base text-gray-400 hover:underline"
            >
              <svg
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                className="inline-block align-middle"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Change profile picture
              <input
                id="profile-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={updateProfilePicture}
              />
            </label>
          </div>
        </div>
        {photoUploadError && (
          <div className="mb-3 rounded bg-red-100 px-3 py-2 text-sm text-red-700 md:hidden">
            {photoUploadError}
          </div>
        )}
        {items.length > 0 && (
          <div className="mb-2 flex w-full flex-col border-t border-gray-300">
            {items.map((item, index) => (
              <SidebarItem
                key={index}
                {...item}
                itemColor="text-black"
                isActive={item.href.startsWith('#') ? item.href === activeHref : undefined}
              />
            ))}
          </div>
        )}
        <hr className="my-4 w-full border-t border-gray-300" />
        {isAdmin() && isUserPage() && (
          <Link
            to={AppRoutes.Users}
            className="mb-4 flex items-center gap-2 text-base hover:underline focus:outline-none"
          >
            Back to Users
          </Link>
        )}
        <button
          onClick={logout}
          className="mb-6 flex items-center gap-2 text-base font-normal text-black hover:underline focus:outline-none"
        >
          <img src={`${imagesBasePath}/logout.svg`} alt="logout" />
          Log out
        </button>
      </div>

      <nav
        className={`${backgroundColor} sticky top-0 z-10 flex hidden h-fit w-[260px] flex-col px-8 pt-8 transition-all duration-300 md:block`}
      >
        <div className="mb-6 flex w-full flex-col border-r">
          {items.map((item, index) => (
            <SidebarItem
              key={index}
              {...item}
              itemColor="text-black"
              isActive={item.href.startsWith('#') ? item.href === activeHref : undefined}
            />
          ))}
        </div>
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500">
            <img src={buildAuthenticatedFileUrl(avatarUrl) || `${imagesBasePath}/default-avatar.svg`} alt="avatar" className="h-full w-full rounded-full object-cover" />
          </div>
          <div className="flex flex-col">
            <span className="text-base font-semibold leading-tight text-black">{userName}</span>
            <label
              htmlFor="profile-upload"
              className="flex cursor-pointer items-center gap-1 text-xs text-gray-400 hover:underline"
            >
              <svg
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                className="inline-block align-middle"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Change profile picture
              <input
                id="profile-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={updateProfilePicture}
              />
            </label>
          </div>
        </div>
        {photoUploadError && (
          <div className="mb-4 rounded bg-red-100 px-3 py-2 text-xs text-red-700">
            {photoUploadError}
          </div>
        )}
        {isAdmin() && isUserPage() && (
          <Link
            to={AppRoutes.Users}
            className="mb-4 flex items-center gap-2 text-base hover:underline focus:outline-none"
          >
            Back to Users
          </Link>
        )}
        <button
          onClick={logout}
          className="mb-6 flex items-center gap-2 text-base font-normal text-black hover:underline focus:outline-none"
        >
          <img src={`${imagesBasePath}/logout.svg`} alt="logout" />
          Log out
        </button>
      </nav>
    </div>
  )
}

export default Sidebar
